import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../../core/services/api.config';

export const CASE_REFERRAL_SOURCE_OPTIONS = [
  'hospital',
  'school',
  'community_based_organization',
  'self',
  'other',
] as const;

export type CaseReferralSource = (typeof CASE_REFERRAL_SOURCE_OPTIONS)[number];

export const CASE_STATUS_OPTIONS = ['open', 'closed', 'declined_closed'] as const;
export type CaseStatus = (typeof CASE_STATUS_OPTIONS)[number];

export const CASE_NOTE_CONTACT_TYPE_OPTIONS = ['street_outreach', 'home_visit', 'phone', 'text'] as const;
export type CaseNoteContactType = (typeof CASE_NOTE_CONTACT_TYPE_OPTIONS)[number];

export const CASE_NOTE_LOCATION_TYPE_OPTIONS = ['community', 'school', 'hospital', 'virtual'] as const;
export type CaseNoteLocationType = (typeof CASE_NOTE_LOCATION_TYPE_OPTIONS)[number];

export interface CaseApiModel {
  id: number;
  caseUniqueId: string;
  clientId: number;
  assignedPartnerOrganizationId: number | null;
  serviceId: number | null;
  serviceName: string | null;
  partnerName: string | null;
  referralSource: CaseReferralSource;
  referralSourceOther: string | null;
  referralDate: string;
  status: CaseStatus;
  closureSummary: string | null;
  priority: 'low' | 'medium' | 'high';
  openedAt: string;
  closedAt: string | null;
  createdByUserId: number;
  createdByName: string | null;
  updatedAt: string;
}

export interface CreateCaseRequest {
  clientId: number;
  assignedPartnerOrganizationId: number;
  serviceId: number;
  referralSource: CaseReferralSource;
  referralSourceOther?: string;
  referralDate: string;
  status?: CaseStatus;
  priority?: 'low' | 'medium' | 'high';
}

export interface CreateCaseResponse {
  data: CaseApiModel;
}

export interface GetCaseResponse {
  data: CaseApiModel;
}

export interface UpdateCaseStatusRequest {
  status: CaseStatus;
  closureSummary?: string;
}

export interface UpdateCaseStatusResponse {
  data: CaseApiModel;
}

export interface CaseNoteApiModel {
  id: number;
  caseId: number;
  authorUserId: number;
  authorName: string;
  note: string;
  noteDate: string | null;
  startTime: string | null;
  endTime: string | null;
  durationMinutes: number;
  contactType: CaseNoteContactType | null;
  locationType: CaseNoteLocationType | null;
  followUpRequired: boolean;
  serviceUnitOfMeasure: string | null;
  serviceUnits: number | null;
  createdAt: string;
}

export interface GetCaseNotesResponse {
  data: CaseNoteApiModel[];
  count: number;
}

export interface CaseStatusEventApiModel {
  id: number;
  caseId: number;
  actorUserId: number;
  actorName: string;
  fromStatus: CaseStatus | null;
  toStatus: CaseStatus;
  summary: string | null;
  createdAt: string;
}

export interface GetCaseStatusEventsResponse {
  data: CaseStatusEventApiModel[];
  count: number;
}

export interface CreateCaseNoteRequest {
  note: string;
  noteDate?: string;
  startTime?: string;
  endTime?: string;
  contactType: CaseNoteContactType;
  locationType: CaseNoteLocationType;
  followUpRequired: boolean;
  serviceUnitOfMeasure?: string;
  serviceUnits?: number;
}

export interface CreateCaseNoteResponse {
  data: CaseNoteApiModel;
}

export interface ServiceOption {
  id: number;
  name: string;
  unitLabel: string;
}

export interface PartnerOption {
  id: number;
  name: string;
}

interface ServicesApiResponse {
  data: Array<{
    id: number;
    name: string;
    service_unit_label?: string | null;
  }>;
}

interface PartnersApiResponse {
  data: Array<{
    id: number;
    name: string;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  createCase(payload: CreateCaseRequest): Observable<CreateCaseResponse> {
    return this.http.post<CreateCaseResponse>(`${this.apiBaseUrl}/cases`, payload, {
      withCredentials: true,
    });
  }

  getCase(caseId: number): Observable<GetCaseResponse> {
    return this.http.get<GetCaseResponse>(`${this.apiBaseUrl}/cases/${caseId}`, {
      withCredentials: true,
    });
  }

  updateCaseStatus(
    caseId: number,
    payload: UpdateCaseStatusRequest,
  ): Observable<UpdateCaseStatusResponse> {
    return this.http.patch<UpdateCaseStatusResponse>(
      `${this.apiBaseUrl}/cases/${caseId}`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  listCaseNotes(caseId: number): Observable<GetCaseNotesResponse> {
    return this.http.get<GetCaseNotesResponse>(`${this.apiBaseUrl}/cases/${caseId}/notes`, {
      withCredentials: true,
    });
  }

  listCaseStatusEvents(caseId: number): Observable<GetCaseStatusEventsResponse> {
    return this.http.get<GetCaseStatusEventsResponse>(
      `${this.apiBaseUrl}/cases/${caseId}/status-events`,
      {
        withCredentials: true,
      },
    );
  }

  createCaseNote(
    caseId: number,
    payload: CreateCaseNoteRequest,
  ): Observable<CreateCaseNoteResponse> {
    return this.http.post<CreateCaseNoteResponse>(`${this.apiBaseUrl}/cases/${caseId}/notes`, payload, {
      withCredentials: true,
    });
  }

  listServices(): Observable<ServicesApiResponse> {
    return this.http.get<ServicesApiResponse>(`${this.apiBaseUrl}/services`, {
      withCredentials: true,
    });
  }

  listPartners(): Observable<PartnersApiResponse> {
    return this.http.get<PartnersApiResponse>(`${this.apiBaseUrl}/partners`, {
      withCredentials: true,
    });
  }

  listApprovedPartnersForService(serviceId: number): Observable<PartnersApiResponse> {
    return this.http.get<PartnersApiResponse>(`${this.apiBaseUrl}/services/${serviceId}/partners`, {
      withCredentials: true,
    });
  }
}
